import { CalendarDateEditIcon } from 'components/icons'
import { DotSolidIcon } from 'components/icons/DotIcon'
import React, { useRef } from 'react'
import {
  CalendarGridProps,
  CalendarState,
  CalendarType,
  CarpoolRequestState,
  CarpoolState,
  isCalendarTypeCarpoolEventData,
  isCalendarTypeCarpoolRequestEventData,
} from 'types/CalendarTypes'
import { getLocalDate, getTimeString } from 'utils/constants'
import CalendarCellComponent from './CalendarCellComponent'

// CalendarGrid: Renders a month grid with events and selection logic
/**
 * CalendarGrid displays a grid of days for a given month, including events and selection state.
 * Handles scrolling to selected dates and visual feedback for different event types.
 */
const CalendarGridComponent = ({
  type,
  gridEvents,
  daysOfWeek,
  selectedDates,
  onClick,
}: {
  type: CalendarType
  gridEvents: CalendarGridProps[]
  daysOfWeek: string[]
  selectedDates: string[]
  onClick?: (date: string, state: CalendarState) => void
}) => {
  // Width of each day cell (as a percentage)
  const cellWidth: string = `${100 / daysOfWeek.length}%`

  // Today's date in YYYY-MM-DD format for comparison
  const today: string = getLocalDate()

  // Store refs for each cell to enable smooth scrolling to selection
  const cellRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Color legend for each type
  const seekingCarpoolColor = '#F97316', // Seeking carpool - Orange
    drivingColor = '#0D9488', // Driving - Dark green
    inACarpoolColor = '#22C55E', // In a carpool - Light green
    someoneElseDrivingColor = '#86EFAC', // Someone else is driving - Lighter green
    notCarpoolingColor = '#6B7281' // Not carpooling - Gray
  const cellStates: {
    [CalendarType.CARPOOL_REQUEST]: Record<CarpoolRequestState, { color: string; icon?: JSX.Element }>
    [CalendarType.CARPOOL]: Record<CarpoolState, { color: string; icon?: JSX.Element }>
  } = {
    [CalendarType.CARPOOL_REQUEST]: {
      [CarpoolRequestState.SEEKING_CARPOOL]: { color: seekingCarpoolColor, icon: <DotSolidIcon /> },
      [CarpoolRequestState.IN_A_CARPOOL]: { color: inACarpoolColor, icon: <DotSolidIcon /> },
      [CarpoolRequestState.NOT_CARPOOLING]: { color: notCarpoolingColor },
    },
    [CalendarType.CARPOOL]: {
      [CarpoolState.YOU_ARE_DRIVING]: { color: drivingColor, icon: <DotSolidIcon /> },
      [CarpoolState.SOMEONE_ELSE_DRIVING]: { color: someoneElseDrivingColor, icon: <DotSolidIcon /> },
      [CarpoolState.NOT_CARPOOLING]: { color: notCarpoolingColor },
    },
  }

  return (
    <>
      {/* Render day-of-week headers */}
      {daysOfWeek.map((day, idx) => (
        <div key={idx} style={{ width: cellWidth }} className='pb-3 pt-1 flex items-center justify-center text-sm p-0'>
          {day}
        </div>
      ))}

      {/* Render each calendar cell */}
      {gridEvents.map(({ eventDate, eventData }, i) => {
        // Check if the date is selected
        const isSelected = Boolean(selectedDates.includes(eventDate))
        // If the date is in the past, disable selection
        let isDisableSelection = Boolean(eventDate < today)

        // Get the cell's content, its state and style
        let cellState: CalendarState | undefined = undefined
        let content: string | JSX.Element | undefined = undefined
        let contentColor: string | undefined = undefined
        switch (type) {
          case CalendarType.CARPOOL_REQUEST:
            if (!eventData) cellState = CarpoolRequestState.NOT_CARPOOLING
            else if (!isCalendarTypeCarpoolRequestEventData(eventData)) cellState = CarpoolRequestState.SEEKING_CARPOOL
            else {
              const { carpoolTime, isCarpoolRequest } = eventData
              cellState =
                carpoolTime >= 0
                  ? isCarpoolRequest
                    ? CarpoolRequestState.SEEKING_CARPOOL
                    : CarpoolRequestState.IN_A_CARPOOL
                  : CarpoolRequestState.NOT_CARPOOLING
              content = cellStates[type][cellState].icon
              if (cellState === CarpoolRequestState.SEEKING_CARPOOL)
                content = getTimeString(carpoolTime) // Show time only if its a carpool request
              else if (cellState === CarpoolRequestState.IN_A_CARPOOL) isDisableSelection = true // Disable selection if its a carpool
            }
            contentColor = cellStates[type][cellState].color
            break
          case CalendarType.CARPOOL:
            if (!eventData) cellState = CarpoolState.NOT_CARPOOLING
            else if (!isCalendarTypeCarpoolEventData(eventData)) cellState = CarpoolState.NOT_CARPOOLING
            else {
              const { carpoolTime, isCarpoolRequest, carpoolDriver } = eventData
              cellState =
                carpoolTime >= 0
                  ? isCarpoolRequest
                    ? CarpoolState.NOT_CARPOOLING
                    : carpoolDriver?.isCurrentUser
                      ? CarpoolState.YOU_ARE_DRIVING
                      : CarpoolState.SOMEONE_ELSE_DRIVING
                  : CarpoolState.NOT_CARPOOLING
              content = cellStates[type][cellState].icon
            }
            contentColor = cellStates[type][cellState].color
            break
        }

        return (
          <CalendarCellComponent
            key={i}
            date={eventDate}
            width={cellWidth}
            content={isSelected ? <CalendarDateEditIcon /> : content}
            contentColor={isSelected ? undefined : contentColor}
            backgroundColor={isSelected ? '#E3FCE3' : 'transparent'}
            // Disable selection for past dates
            cursor={!isDisableSelection ? 'pointer' : 'default'}
            opacity={isDisableSelection ? 'opacity-50' : ''}
            onClick={onClick && !isDisableSelection ? () => onClick(eventDate, cellState) : undefined}
            innerRef={(el) => (cellRefs.current[eventDate] = el)}
          />
        )
      })}

      {/* Re-render the legend */}
      <div className='w-full mt-2 md:mt-4 p-4 border-t flex items-center justify-center gap-x-4 gap-y-2 flex-wrap'>
        {Object.entries(cellStates[type])
          .filter(([_, state]) => state.icon)
          .map(([label, state], idx: number) => (
            <div className='flex items-center' key={idx}>
              {state.icon && React.cloneElement(state.icon, { className: 'w-3 h-3', color: state.color })}
              <span className='ml-2 text-xs md:text-sm text-gray-600'>{label}</span>
            </div>
          ))}
      </div>
    </>
  )
}

export default CalendarGridComponent
